import { ApiResponse } from "../api";
import { apiQueryAdapter } from "../apiQueryAdapter";
import { PageResponseModel } from "../common/models";
import { AddressChangeLog, AddressCounts, AddressDetailsModel, AddressesListModel, AddressNote, AppointmentResponse, AssociatedAccount, ProximityResponse, tableSize, VerifiedDetailsResponse } from "./models";

class AddressmanagerService {
  private static instance: AddressmanagerService;

  private constructor() { }

  static getInstance(): AddressmanagerService {
    if (!AddressmanagerService.instance) {
      AddressmanagerService.instance = new AddressmanagerService();
    }
    return AddressmanagerService.instance;
  }

  public getListAddressesNearTerminal = async (terminal: string, search: string, city: string, state: string, zip: string, page: number, flag: string): Promise<PageResponseModel<AddressesListModel>> => {
    const params = new URLSearchParams();
    params.set("size", tableSize.toString());
    if (terminal) {
      params.set("terminal", terminal);
    }
    if (search) {
      params.set("search", search);
    }
    if (city) {
      params.set("city", city);
    }
    if (state) {
      params.set("state", state);
    }
    if (zip) {
      params.set("zip", zip);
    }
    if (page) {
      params.set("page", page.toString());
    }
    if (flag) {
      params.set("flag", flag);
    }
    const response = await apiQueryAdapter.query(
      "/address",
      "GET",
      { params }
    );

    // Extract the data from the ApiResponse and ensure it matches PageResponseModel
    return {
      content: response.data.content || [],
      pageable: {
        pageNumber: response.data.pageable?.pageNumber || 0
      },
      last: response.data.last || false
    };
  };

  public getAddressById = async (addressId: string): Promise<AddressDetailsModel> => {
    const response = await apiQueryAdapter.query(
      `/address/${addressId}`,
      "GET"
    );
    return response.data;
  };
  
  public createAddress = async (data: AddressDetailsModel | undefined):
    Promise<ApiResponse<AddressDetailsModel>> => {
    if (!data) throw new Error("Data is required");

    const response = await apiQueryAdapter.query<ApiResponse<AddressDetailsModel>>(
      `/address`,
      "POST",
      { body: data }
    );
    return response.data;
  };

  public updateAddress = async (data: AddressDetailsModel | undefined, id: string | undefined): Promise<ApiResponse<AddressDetailsModel>> => {
    if (!data) {
      throw new Error("Data is required");
    }
    if (!id) {
      throw new Error("Address Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/${id}`,
      "PUT",
      { body: data }
    );
    return response.data;
  };

  public getNotesByAddressId = async (addressId: string | undefined, page: number, filter: string): Promise<PageResponseModel<AddressNote>> => {
    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const params = new URLSearchParams();
    params.set("size", tableSize.toString());
    params.set("page", page.toString());
    if (filter) {
      params.set("search", filter);
    }
    const response = await apiQueryAdapter.query(
      `/address/address-note/${addressId}`,
      "GET",
      { params }
    );
    return response.data;
  };

  public createNotes = async (data: AddressNote | undefined): Promise<ApiResponse<AddressNote>> => {
    if (!data) {
      throw new Error("Data is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/address-note`,
      "POST",
      { body: data }
    );
    return response.data;
  };

  public updateNotes = async (data: AddressNote | undefined): Promise<ApiResponse<AddressNote>> => {
    if (!data) {
      throw new Error("Data is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/address-note/${data.id}`,
      "PUT",
      { body: data }
    );
    return response.data;
  };

  public deleteNotes = async (noteId: string | undefined): Promise<ApiResponse<any>> => {
    if (!noteId) {
      throw new Error("Note Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/address-note/${noteId}`,
      "DELETE"
    );
    return response;
  };

  public getChangeLog = async (addressId: string | undefined | null, page: number, from: string | undefined, to: string | undefined): Promise<PageResponseModel<AddressChangeLog>> => {
    const params = new URLSearchParams();
    params.set("size", tableSize.toString());
    params.set("page", page.toString());
    if (!!from) {
      params.set("from", from);
    }
    if (!!to) {
      params.set("to", to);
    }

    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/change-log/${addressId}`,
      "GET",
      { params }
    );
    return response.data;
  };

  public getAssociatedAccounts = async (addressId: string | undefined | null, search: string, page: number): Promise<PageResponseModel<AssociatedAccount>> => {
    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const params = new URLSearchParams();
    params.set("size", tableSize.toString());
    params.set("page", page.toString());
    if (search) {
      params.set("accountName", search);
    }
    const response = await apiQueryAdapter.query(
      `/address/${addressId}/accounts`,
      "GET",
      { params }
    );
    return response.data;
  };

  public getNearbyDetails = async (addressId: string | undefined | null): Promise<ProximityResponse> => {
    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/${addressId}/nearby`,
      "GET"
    );
    return response.data;
  }

  public getVerifiedDetails = async (addressId: string | undefined | null): Promise<VerifiedDetailsResponse> => {
    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/${addressId}/verification`,
      "GET"
    );
    return response.data;
  }

  public deleteAddress = async (addressId: string | undefined): Promise<ApiResponse<any>> => {
    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/address/${addressId}`,
      "DELETE"
    );
    return response;
  };

  public getAddressCounts = async (): Promise<AddressCounts> => {
    const response = await apiQueryAdapter.query(
      `/address/counts`,
      "GET"
    );
    return response.data;
  }

  public getUpcomingDetails = async (addressId: string | undefined | null): Promise<AppointmentResponse> => {
    if (!addressId) {
      throw new Error("Address Id is required");
    }
    const response = await apiQueryAdapter.query(
      `/appointments/${addressId}/upcoming`,
      "GET"
    );
    return response.data;
  }
}

export default AddressmanagerService;
